import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    num: '01',
    title: '現状診断',
    sub: '会議の可視化・問題構造の明確化',
    body: '現在の会議実態をヒアリング・観察で可視化。どこに無駄が生じているか、意思決定がどこで詰まっているかを構造的に診断します。',
    points: ['会議ログの分析', '参加者へのヒアリング', '問題マッピング'],
    duration: '2〜3週間',
  },
  {
    num: '02',
    title: '会議設計',
    sub: 'アジェンダ設計・意思決定プロセスの再構築',
    body: '目的・権限・ルール・タイムボックスを定義し、会議の「型」を作ります。始まる前に8割が決まる状態を設計します。',
    points: ['会議種別の整理', 'アジェンダテンプレート作成', '意思決定マトリクス策定'],
    duration: '3〜4週間',
  },
  {
    num: '03',
    title: 'AI連携',
    sub: '議事録・タスク管理の自動化',
    body: '会議の録音・文字起こし・議事録生成・タスク割り振りをAIで自動化。人の手を介さず「決定→実行」へ直結します。',
    points: ['AI議事録の導入', 'タスク自動割り振り', 'Slack/Notionとの連携'],
    duration: '2〜3週間',
  },
  {
    num: '04',
    title: '定着支援',
    sub: '組織への根付き・チェンジマネジメント',
    body: '仕組みだけ作っても組織は変わりません。管理職へのコーチングと定期レビューで、新しい会議文化を組織に根付かせます。',
    points: ['管理職向けトレーニング', '月次レビューミーティング', '3ヶ月フォローアップ'],
    duration: '継続サポート',
  },
];

export default function ServiceSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.06 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} id="service" className="py-24 px-4 md:px-10 bg-white">
      <div className="max-w-5xl mx-auto">
        <div
          className="text-center mb-14 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <span
            className="inline-block px-3 py-1 text-xs font-bold tracking-widest uppercase text-[#C0392B] border border-[#C0392B]/30 rounded-full mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Service
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            会議設計<br className="hidden md:block" />サービス内容
          </h2>
          <p className="text-sm text-gray-500" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            診断から定着まで、4ステップで会議を根本から変える
          </p>
        </div>

        <div className="space-y-4">
          {steps.map((s, i) => (
            <div
              key={i}
              className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-4 p-6 md:p-8 rounded-lg border border-gray-200 items-start transition-all duration-700 hover:border-[#C0392B]/20"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateX(0)' : 'translateX(-20px)',
                transitionDelay: `${i * 0.1}s`,
              }}
            >
              {/* Step number */}
              <div className="flex-shrink-0">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#C0392B]/10">
                  <span
                    className="text-sm font-bold text-[#C0392B]"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {s.num}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div>
                <h3
                  className="text-base font-bold text-gray-900 mb-0.5"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  {s.title}
                </h3>
                <p className="text-xs text-gray-400 mb-3" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  {s.sub}
                </p>
                <p className="text-sm text-gray-600 leading-relaxed mb-3" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  {s.body}
                </p>
                <div className="flex flex-wrap gap-2">
                  {s.points.map((pt) => (
                    <span
                      key={pt}
                      className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded-full"
                      style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                    >
                      {pt}
                    </span>
                  ))}
                </div>
              </div>

              {/* Duration */}
              <div className="flex-shrink-0 text-right">
                <span
                  className="text-xs text-gray-400 whitespace-nowrap"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  期間
                </span>
                <p
                  className="text-sm font-bold text-gray-700 whitespace-nowrap"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  {s.duration}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
